/* eslint-disable no-magic-numbers -- crypto constants and buffer sizes */
/* eslint-disable @typescript-eslint/class-methods-use-this -- for now*/
import { type KEM, ml_kem768 } from '@noble/post-quantum/ml-kem';
import argon2 from 'argon2-browser/dist/argon2-bundled.min.js';

import {
  AES_KEY_SIZE_BYTES,
  AES_KEY_SIZE_BITS,
  AES_IV_SIZE_BYTES,
  AES_AUTH_TAG_SIZE_BYTES,
  AES_AUTH_TAG_SIZE_BITS,
  ARGON2_TIME_COST,
  ARGON2_MEMORY_COST,
  ARGON2_HASH_LENGTH,
  ARGON2_PARALLELISM,
  HEX_RADIX,
  HEX_PAD_LENGTH,
  HEX_PAD_CHAR,
  MLKEM_SEED_SIZE_BYTES,
} from '@/constants/crypto.constants';
import { createLogger, type Logger } from '@/utils/logger';

import {
  CryptoService,
  type MLKEMKeyPair,
  type EncapsulationResult,
  type DecapsulationResult,
  type PasswordHashResult,
  type HashResult,
  type AesGcmEncryptionResult
} from '../abstract/poc/crypto.service.interface';

const log: Logger = createLogger({ namespace: 'six3ro:crypto' });

export async function encryptWithAesGcm(
    data: Uint8Array,
    key: Uint8Array
  ): Promise<AesGcmEncryptionResult> {
    // Generate a random IV for AES-GCM
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Ensure the key and data have proper ArrayBuffer (not SharedArrayBuffer)
    const keyBuffer = new Uint8Array(key);
    const dataBuffer = new Uint8Array(data);

    // Create a crypto key from the key
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt']
    );

    // Encrypt the data
    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, tagLength: 128 }, // 128-bit auth tag
      cryptoKey,
      dataBuffer
    );

    // Split the result into ciphertext and auth tag
    const encryptedArray = new Uint8Array(encryptedBuffer);
    const authTagLength = 16; // 128 bits = 16 bytes

    // Extract the auth tag (last 16 bytes)
    const authTag = encryptedArray.slice(encryptedArray.length - authTagLength);

    // Extract the actual ciphertext (everything except the auth tag)
    const encryptedData = encryptedArray.slice(0, encryptedArray.length - authTagLength);

    return { encryptedData, iv, authTag };
  }

/**
 * Corrected CryptoService implementation addressing ESLint and TypeScript issues
 */
export class CryptoServiceImpl extends CryptoService {
  private readonly mlkem: KEM;

  constructor(mlkem: KEM = ml_kem768) {
    super();
    this.mlkem = mlkem;
  }

  // ============= KEY GENERATION =============

  public generateAesKey(): string {
    try {
      // Using crypto.getRandomValues for better randomness (uses 'this' indirectly through instance context)
      const randomBytes = this.randomBytes(AES_KEY_SIZE_BYTES);
      return this.bytesToHex(randomBytes);
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('AES key generation', error);
    }
  }

  public async generateMlKemKeyPair(seed?: Uint8Array): Promise<MLKEMKeyPair> {
    try {
      const keypair = this.mlkem.keygen(seed);
      return await Promise.resolve({
        privateKey: keypair.secretKey,
        publicKey: keypair.publicKey
      });
    } catch (error) {
      CryptoServiceImpl.handleCryptoError('ML-KEM key pair generation', error);
    }
  }

  public async generateMlKemKeyPairFromCredentials(
    email: string,
    password: string
  ): Promise<MLKEMKeyPair> {
    try {
      // Derive a consistent seed from email+password using Argon2id
      // ML-KEM-768 requires a 64-byte seed for deterministic key generation
      const passwordHashResult = await this.hashPassword(password, email);

      // Argon2 gives us 32 bytes, we need 64 bytes for ML-KEM
      // Expand the seed by concatenating the Argon2 hash with its SHA-512 hash
      const argon2Hash = passwordHashResult.hash; // 32 bytes
      const sha512Result = await this.hashSha512(this.bytesToHex(argon2Hash)); // Another 64 bytes

      // Take first 32 bytes from Argon2 + first 32 bytes from SHA-512 = 64 bytes total
      const seed = new Uint8Array(MLKEM_SEED_SIZE_BYTES);
      seed.set(argon2Hash, 0); // First 32 bytes
      seed.set(sha512Result.hash.slice(0, 32), 32); // Next 32 bytes

      return await this.generateMlKemKeyPair(seed);
    } catch (error) {
      CryptoServiceImpl.handleCryptoError('ML-KEM key pair generation from credentials', error);
    }
  }

  // ============= KEY ENCAPSULATION =============

  public encapsulateMlKem(
    publicKey: Uint8Array,
    seed?: Uint8Array
  ): EncapsulationResult {
    try {
      const result = this.mlkem.encapsulate(publicKey, seed);
      return {
        cipherText: result.cipherText,
        sharedSecret: result.sharedSecret
      };
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('ML-KEM encapsulation', error);
    }
  }

  public decapsulateMlKem(
    cipherText: Uint8Array,
    privateKey: Uint8Array
  ): DecapsulationResult {
    try {
      const sharedSecret = this.mlkem.decapsulate(cipherText, privateKey);
      return {
        cipherText,
        sharedSecret
      };
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('ML-KEM decapsulation', error);
    }
  }

  // ============= SYMMETRIC ENCRYPTION =============

  public async encrypt(
    data: Uint8Array,
    key: Uint8Array
  ): Promise<AesGcmEncryptionResult> {
    try {
      const { iv, cryptoKey } = await CryptoServiceImpl.prepareEncryption(key);
      const encryptedBuffer = await CryptoServiceImpl.performEncryption(data, cryptoKey, iv);
      return CryptoServiceImpl.extractEncryptionComponents(encryptedBuffer, iv);
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('AES-GCM encryption', error);
    }
  }

  public async decrypt(
    encryptedData: Uint8Array,
    iv: Uint8Array,
    authTag: Uint8Array,
    key: Uint8Array
  ): Promise<Uint8Array> {
    try {
      const cryptoKey = await CryptoServiceImpl.prepareCryptoKey(key, ['decrypt']);
      const combinedData = CryptoServiceImpl.combineEncryptedDataWithTag(encryptedData, authTag);
      const decryptedBuffer = await CryptoServiceImpl.performDecryption(combinedData, cryptoKey, iv);
      return new Uint8Array(decryptedBuffer);
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('AES-GCM decryption', error);
    }
  }

  // ============= HASHING =============

  public async hashSha256(
    data: File | Blob | ArrayBuffer | Uint8Array
  ): Promise<Uint8Array> {
    try {
      const arrayBuffer = await CryptoServiceImpl.convertToArrayBuffer(data);
      const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
      return new Uint8Array(hashBuffer);
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('SHA-256 hashing', error);
    }
  }

  public async hashSha512(input: string): Promise<HashResult> {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(input);
      const hashBuffer = await crypto.subtle.digest('SHA-512', data);
      const hashArray = new Uint8Array(hashBuffer);
      const hexString = CryptoServiceImpl.convertToHexString(hashArray);

      return {
        hash: hashArray,
        hexString
      };
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('SHA-512 hashing', error);
    }
  }
  public async  encryptPrivateKey(privateKey: Uint8Array, ukek: {
    sha512Hash: string;
    keyHex: string;
    encoded: string;
    hash: Uint8Array;
}
) {
    const { encryptedData, iv, authTag } = await encryptWithAesGcm(privateKey, ukek.hash);
    // Store: IV (12 bytes) + encrypted data + auth tag (16 bytes)
    const result = new Uint8Array(iv.length + encryptedData.byteLength + authTag.length);
    result.set(iv);
    result.set(new Uint8Array(encryptedData), iv.length);
    result.set(authTag, iv.length + encryptedData.byteLength);

    return Array.from(result)
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  public async decryptPrivateKey(encryptedPrivateKeyHex: string, ukekHash: Uint8Array): Promise<Uint8Array> {
    try {
      log.debug('Attempting private key decryption...');
      log.debug('Input hex length:', encryptedPrivateKeyHex.length);
      log.debug('UKEK hash length:', ukekHash.length);

      // Convert hex string to bytes
      const encryptedBytes = new Uint8Array(
        encryptedPrivateKeyHex.match(/.{1,2}/g)?.map(byte => Number.parseInt(byte, 16)) ?? []
      );
      log.debug('Encrypted bytes length:', encryptedBytes.length);

      const IV_LENGTH_12 = 12;  // Current standard
      const IV_LENGTH_16 = 16;  // Legacy format
      const AUTH_TAG_LENGTH = 16;

      log.debug('Format analysis: Total bytes:', encryptedBytes.length);

      // Try different IV lengths - check which format this might be
      const possibleFormats = [
        { ivLength: IV_LENGTH_12, name: '12-byte IV (current)' },
        { ivLength: IV_LENGTH_16, name: '16-byte IV (legacy)' }
      ];

      for (const format of possibleFormats) {
        const minLengthWithAuthTag = format.ivLength + AUTH_TAG_LENGTH + 1;

        log.debug(`Trying ${format.name} - Min length:`, minLengthWithAuthTag);

        // Try new format first (IV + encrypted data + auth tag)
        if (encryptedBytes.length >= minLengthWithAuthTag) {
          log.debug(`Attempting new format with ${format.name}...`);
          try {
            const iv = encryptedBytes.slice(0, format.ivLength);
            const authTag = encryptedBytes.slice(-AUTH_TAG_LENGTH); // Last 16 bytes
            const encryptedData = encryptedBytes.slice(format.ivLength, -AUTH_TAG_LENGTH); // Middle part

            log.debug('Components:', { iv: iv.length, data: encryptedData.length, authTag: authTag.length });

            // Decrypt using the standard decrypt method with proper auth tag
            const result = await this.decrypt(encryptedData, iv, authTag, ukekHash);
            log.info(`New format decryption succeeded with ${format.name}`);
            return result;
          } catch (newFormatError) {
            log.warn(`New format with ${format.name} failed:`, newFormatError);
          }
        }

        // Try legacy format (IV + encrypted data, no auth tag)
        if (encryptedBytes.length >= format.ivLength + 1) {
          log.debug(`Attempting legacy format with ${format.name}...`);

          const iv = encryptedBytes.slice(0, format.ivLength);
          const encryptedData = encryptedBytes.slice(format.ivLength); // All remaining bytes

          log.debug('Legacy components:', { iv: iv.length, data: encryptedData.length });

          try {
            const result = await this.decryptLegacyFormat(encryptedData, iv, ukekHash);
            log.info(`Legacy format decryption succeeded with ${format.name}`);
            return result;
          } catch (legacyError) {
            log.error(`Legacy format with ${format.name} failed:`, legacyError);
          }
        }
      }

      throw new Error('All decryption formats failed - invalid encrypted private key data');

    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('Private key decryption', error);
    }
  }

  /**
   * Decrypt legacy private key format (without auth tag)
   * Used for backward compatibility with keys encrypted before auth tag was added
   */
  private async decryptLegacyFormat(encryptedData: Uint8Array, iv: Uint8Array, key: Uint8Array): Promise<Uint8Array> {
    try {
      log.debug('Legacy decryption starting', { data: encryptedData.length, iv: iv.length, key: key.length });

      // Create proper ArrayBuffer for key (same pattern as existing code)
      const keyBuffer = new ArrayBuffer(key.length);
      const keyView = new Uint8Array(keyBuffer);
      keyView.set(key);

      // Create proper ArrayBuffer for IV
      const ivBuffer = new ArrayBuffer(iv.length);
      const ivView = new Uint8Array(ivBuffer);
      ivView.set(iv);

      // Create proper ArrayBuffer for encrypted data
      const dataBuffer = new ArrayBuffer(encryptedData.length);
      const dataView = new Uint8Array(dataBuffer);
      dataView.set(encryptedData);

      log.debug('ArrayBuffers created', { key: keyBuffer.byteLength, iv: ivBuffer.byteLength, data: dataBuffer.byteLength });

      // Import the key
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyBuffer,
        { name: 'AES-GCM', length: 256 },
        false,
        ['decrypt']
      );

      // For legacy format, we decrypt the data as-is
      // The original encryptWithAesGcm should have included the auth tag in the encrypted data
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: ivBuffer,
          tagLength: 128
        },
        cryptoKey,
        dataBuffer
      );

      return new Uint8Array(decryptedBuffer);
    } catch (error) {
      throw new Error(`Legacy format decryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // ============= PASSWORD OPERATIONS =============

  public async hashPassword(password: string, emailForSalt: string): Promise<PasswordHashResult> {
    try {
      const { hash: salt, hexString: sha512Hash } = await this.hashSha512(password + emailForSalt);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- issue with argon2 import
      const argon2Result = await CryptoServiceImpl.performArgon2Hashing(password, salt);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access -- issue with argon2 import
      const keyHex = CryptoServiceImpl.convertToHexString(argon2Result.hash);

      return {
        sha512Hash,
        keyHex,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- issue with argon2 import
        encoded: argon2Result.encoded,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- issue with argon2 import
        hash: argon2Result.hash
      };
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('Argon2 password hashing', error);
    }
  }

  public async deriveKey(password: string, email: string): Promise<string> {
    try {
      const result = await this.hashPassword(password, email);
      return result.keyHex;
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('key derivation from password', error);
    }
  }

  // ============= UTILITIES =============

  public hexToBytes(hex: string): Uint8Array {
    CryptoServiceImpl.validateHexString(hex);

    try {
      const bytes = new Uint8Array(hex.length / HEX_PAD_LENGTH);

      for (let i = 0; i < hex.length; i += HEX_PAD_LENGTH) {
        bytes[i / HEX_PAD_LENGTH] = Number.parseInt(
          hex.substring(i, i + HEX_PAD_LENGTH),
          HEX_RADIX
        );
      }

      return bytes;
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('hex to Uint8Array conversion', error);
    }
  }

  public bytesToHex(data: Uint8Array): string {
    try {
      return CryptoServiceImpl.convertToHexString(data);
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('Uint8Array to hex conversion', error);
    }
  }

  public randomBytes(length: number): Uint8Array {
    try {
      return crypto.getRandomValues(new Uint8Array(length));
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('random bytes generation', error);
    }
  }

  // ============= PRIVATE HELPER METHODS =============

  /**
   * Static error handling method (ESLint compliant)
   */
  private static handleCryptoError(operation: string, error: unknown): never {
    const message = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`${operation} failed: ${message}`);
  }

  /**
   * Prepare encryption components (reduced complexity)
   */
  private static async prepareEncryption(key: Uint8Array) {
    const iv = crypto.getRandomValues(new Uint8Array(AES_IV_SIZE_BYTES));
    const cryptoKey = await CryptoServiceImpl.prepareCryptoKey(key, ['encrypt']);
    return { iv, cryptoKey };
  }

  /**
   * Create CryptoKey from raw key material - Fixed TypeScript issues
   */
  private static async prepareCryptoKey(key: Uint8Array, keyUsages: KeyUsage[]): Promise<CryptoKey> {
    // Create proper ArrayBuffer to avoid TypeScript issues
    const keyBuffer = new ArrayBuffer(key.length);
    const keyView = new Uint8Array(keyBuffer);
    keyView.set(key);

    return await crypto.subtle.importKey(
      'raw',
      keyBuffer, // Use ArrayBuffer directly
      { name: 'AES-GCM', length: AES_KEY_SIZE_BITS },
      false,
      keyUsages
    );
  }

  /**
   * Perform AES-GCM encryption - Fixed TypeScript issues
   */
  private static async performEncryption(data: Uint8Array, cryptoKey: CryptoKey, iv: Uint8Array): Promise<ArrayBuffer> {
    // Create proper ArrayBuffer for data
    const dataBuffer = new ArrayBuffer(data.length);
    const dataView = new Uint8Array(dataBuffer);
    dataView.set(data);

    // Create proper ArrayBuffer for IV
    const ivBuffer = new ArrayBuffer(iv.length);
    const ivView = new Uint8Array(ivBuffer);
    ivView.set(iv);

    return await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: ivBuffer, tagLength: AES_AUTH_TAG_SIZE_BITS },
      cryptoKey,
      dataBuffer
    );
  }

  /**
   * Extract encryption components (reduced complexity)
   */
  private static extractEncryptionComponents(encryptedBuffer: ArrayBuffer, iv: Uint8Array): AesGcmEncryptionResult {
    const encryptedArray = new Uint8Array(encryptedBuffer);
    const authTag = encryptedArray.slice(
      encryptedArray.length - AES_AUTH_TAG_SIZE_BYTES
    );
    const encryptedData = encryptedArray.slice(
      0,
      encryptedArray.length - AES_AUTH_TAG_SIZE_BYTES
    );

    return { encryptedData, iv, authTag };
  }

  /**
   * Combine encrypted data with auth tag for decryption
   */
  private static combineEncryptedDataWithTag(encryptedData: Uint8Array, authTag: Uint8Array): ArrayBuffer {
    const combined = new ArrayBuffer(encryptedData.length + authTag.length);
    const combinedView = new Uint8Array(combined);
    combinedView.set(encryptedData, 0);
    combinedView.set(authTag, encryptedData.length);
    return combined;
  }

  /**
   * Perform AES-GCM decryption - Fixed TypeScript issues
   */
  private static async performDecryption(combinedData: ArrayBuffer, cryptoKey: CryptoKey, iv: Uint8Array): Promise<ArrayBuffer> {
    // Create proper ArrayBuffer for IV
    const ivBuffer = new ArrayBuffer(iv.length);
    const ivView = new Uint8Array(ivBuffer);
    ivView.set(iv);

    return await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBuffer, tagLength: AES_AUTH_TAG_SIZE_BITS },
      cryptoKey,
      combinedData
    );
  }

  /**
   * Convert various data types to ArrayBuffer
   */
  private static async convertToArrayBuffer(data: File | Blob | ArrayBuffer | Uint8Array): Promise<ArrayBuffer> {
    if (data instanceof File || data instanceof Blob) {
      return await data.arrayBuffer();
    }

    if (data instanceof Uint8Array) {
      // Create proper ArrayBuffer from Uint8Array
      const buffer = new ArrayBuffer(data.length);
      const view = new Uint8Array(buffer);
      view.set(data);
      return buffer;
    }

    return data;
  }

  /**
   * Perform Argon2 hashing with constants
   */
  private static performArgon2Hashing(password: string, salt: Uint8Array) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- issue with argon2 import
    return argon2.hash({
      pass: password,
      salt,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- issue with argon2 import
      type: argon2.ArgonType.Argon2id,
      time: ARGON2_TIME_COST,
      mem: ARGON2_MEMORY_COST,
      hashLen: ARGON2_HASH_LENGTH,
      parallelism: ARGON2_PARALLELISM,
    });
  }

  /**
   * Convert Uint8Array to hex string (DRY utility)
   */
  private static convertToHexString(data: Uint8Array): string {
    return Array.from(data)
      .map(byte => byte.toString(HEX_RADIX).padStart(HEX_PAD_LENGTH, HEX_PAD_CHAR))
      .join('');
  }

  /**
   * Validate hex string format
   */
  private static validateHexString(hex: string): void {
    if (hex.length % HEX_PAD_LENGTH !== 0) {
      throw new Error('Hex string must have an even number of characters');
    }
  }
}
